<template>
  <div class="match-annotate-page" v-loading="loading">
    <div class="top-section">
      <div class="xray-card">
        <div class="xray-top-left">
          <div class="xray-header">X光图片</div>
          <div class="xray-actions">
            <el-button size="small" @click="backToList">返回</el-button>
          </div>
        </div>
        <div class="card-body" ref="xrayContainer">
          <XrayStrip
            v-if="xrayImage"
            :prev-image="currentData.prevImage"
            :xray-image="xrayImage"
            :next-image="currentData.nextImage"
            :prev-offset-y="currentData.prevOffsetY"
            :current-offset-y="currentData.currentOffsetY"
            :next-offset-y="currentData.nextOffsetY"
            :annotations="xrayAnnoList"
            :selected-id="xraySelectedAnnoId"
            color="#5d89c0"
            selected-color="#ff6b6b"
            :disable-anno-ids="xrayDisabledIds"
            @annoClick="onXrayAnnoClick"
          />
        </div>
      </div>
      <div class="sidebar-card" v-if="currentCamera">
        <div class="sidebar-header">标注组 ({{ currentCameraGroups.length }})</div>
        <div class="sidebar-body">
          <div
            v-for="grp in currentCameraGroups"
            :key="grp.pairId"
            class="group-item"
            :class="{ editing: editingGroup && editingGroup.pairId === grp.pairId }"
          >
            <span class="group-no">#{{ grp.groupNo }}</span>
            <span class="group-info">X:{{ grp.xrayAnnotationId }} ↔ C:{{ grp.cameraAnnotationId }}</span>
            <span class="group-actions">
              <el-button size="mini" type="text" @click="editGroup(grp)">编辑</el-button>
              <el-button size="mini" type="text" style="color:#e02020" @click="deleteGroup(grp)">删除</el-button>
            </span>
          </div>
          <div v-if="currentCameraGroups.length === 0" class="empty-hint">暂无标注组</div>
        </div>
      </div>
    </div>

    <div class="camera-card">
      <div class="card-body" ref="cameraContainer">
        <MatchAnnoPanel
          v-if="currentCamera"
          :src="currentCamera.url"
          :annotations="cameraAnnoList"
          :img-width="currentCamera.width"
          :img-height="currentCamera.height"
          :selected-id="cameraSelectedAnnoId"
          color="#4caf50"
          selected-color="#ff6b6b"
          :disable-anno-ids="cameraDisabledIds"
          :flip-h="currentData.flipH"
          :flip-v="currentData.flipV"
          @annoClick="onCameraAnnoClick"
        />
        <div class="camera-top-left">
          <div class="camera-header">视频图片 {{ currentCameraIndex + 1 }} / {{ cameraCount }}</div>
          <div class="camera-actions">
            <el-button size="small" :disabled="currentCameraIndex <= 0" @click="prevCamera">上一张</el-button>
            <el-button size="small" :disabled="currentCameraIndex >= cameraCount - 1" @click="nextCamera"
              >下一张</el-button
            >
            <el-button
              type="primary"
              size="small"
              :disabled="!xraySelectedAnnoId || !cameraSelectedAnnoId"
              @click="savePair"
              >{{ editingGroup ? '保存修改' : '保存关联' }}</el-button
            >
            <el-button type="success" size="small" :disabled="!isLastCamera" @click="finishImage">完成</el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MatchAnnoPanel from '@/components/MatchAnnoPanel';
import XrayStrip from '@/components/XrayStrip';
import * as api from '@/api/api.js';
export default {
  name: 'MatchTaskAnnotate',
  components: { MatchAnnoPanel, XrayStrip },
  data() {
    return {
      loading: false,
      currentData: null,
      currentCameraIndex: 0,

      xraySelectedAnnoId: null,
      cameraSelectedAnnoId: null,
      editingGroup: null,

      // Per-camera state: cameraImageId -> { groups, xraySelectedAnnoId, cameraSelectedAnnoId, editingGroup }
      cameraStateMap: {},
    };
  },
  computed: {
    matchTaskId() {
      return parseInt(this.$route.params.matchTaskId, 10);
    },
    xrayImage() {
      return this.currentData ? this.currentData.xrayImage : null;
    },
    cameraImages() {
      return this.currentData ? this.currentData.cameraImages : [];
    },
    cameraCount() {
      return this.cameraImages.length;
    },
    currentCamera() {
      return this.cameraImages[this.currentCameraIndex] || null;
    },
    isLastCamera() {
      return this.currentCameraIndex >= this.cameraCount - 1;
    },
    currentCameraId() {
      return this.currentCamera ? this.currentCamera.id : null;
    },
    currentCameraGroups() {
      if (!this.currentCameraId) return [];
      const state = this.cameraStateMap[this.currentCameraId];
      return state ? state.groups : [];
    },
    // Build set of disabled annotation IDs for x-ray (already paired for the current camera)
    xrayDisabledIds() {
      return this.currentCameraGroups.map(g => g.xrayAnnotationId);
    },
    // Build set of disabled annotation IDs for camera (already paired for the current camera)
    cameraDisabledIds() {
      return this.currentCameraGroups.map(g => g.cameraAnnotationId);
    },
    // Annotations with group markers added
    xrayAnnoList() {
      if (!this.xrayImage) return [];
      const groups = this.currentCameraGroups;
      const groupMap = {};
      groups.forEach(g => {
        groupMap[g.xrayAnnotationId] = g.groupNo;
      });
      return this.xrayImage.annotations.map(a => ({ ...a, groupNo: groupMap[a.id] || null }));
    },
    cameraAnnoList() {
      if (!this.currentCamera) return [];
      const groups = this.currentCameraGroups;
      const groupMap = {};
      groups.forEach(g => {
        groupMap[g.cameraAnnotationId] = g.groupNo;
      });
      return this.currentCamera.annotations.map(a => ({ ...a, groupNo: groupMap[a.id] || null }));
    },
  },
  mounted() {
    this.loadImage();
  },
  beforeDestroy() {
    this.saveCurrentCameraState();
  },
  methods: {
    async loadImage() {
      this.loading = true;
      try {
        const res = await api.getMatchTaskAnnotateImage(this.matchTaskId);
        this.currentData = res.data;
        this.currentCameraIndex = 0;
        this.xraySelectedAnnoId = null;
        this.cameraSelectedAnnoId = null;
        this.editingGroup = null;
        this.cameraStateMap = {};

        // Pre-initialize reactive state for all camera images
        for (const cam of this.cameraImages) {
          this.$set(this.cameraStateMap, cam.id, {
            groups: [],
            xraySelectedAnnoId: null,
            cameraSelectedAnnoId: null,
            editingGroup: null,
          });
        }

        // Recover groups from existing pairs
        this.recoverFromPairs(this.currentData.pairs);

        // Navigate to the last camera image that has groups
        let lastIdx = -1;
        for (let i = 0; i < this.cameraImages.length; i++) {
          const state = this.cameraStateMap[this.cameraImages[i].id];
          if (state && state.groups.length > 0) lastIdx = i;
        }
        if (lastIdx >= 0) this.currentCameraIndex = lastIdx;
      } finally {
        this.loading = false;
      }
    },
    recoverFromPairs(pairs) {
      if (!pairs || pairs.length === 0) return;

      // Build annotationId -> cameraImageId map
      const annoToCamera = {};
      for (const cam of this.cameraImages) {
        for (const a of cam.annotations) {
          annoToCamera[a.id] = cam.id;
        }
      }

      // Group pairs by cameraImageId
      const pairGroups = {};
      let groupNo = 1;
      for (const pair of pairs) {
        const camId = annoToCamera[pair.cameraAnnotationId];
        if (!camId) continue;
        if (!pairGroups[camId]) pairGroups[camId] = [];
        pairGroups[camId].push({
          pairId: pair.id,
          groupNo: groupNo++,
          xrayAnnotationId: pair.xrayAnnotationId,
          cameraAnnotationId: pair.cameraAnnotationId,
          cameraImageId: camId,
        });
      }

      // Initialize cameraStateMap for each camera that has groups
      for (const cam of this.cameraImages) {
        if (pairGroups[cam.id]) {
          if (!this.cameraStateMap[cam.id]) {
            this.cameraStateMap[cam.id] = {
              groups: [],
              xraySelectedAnnoId: null,
              cameraSelectedAnnoId: null,
              editingGroup: null,
            };
          }
          this.cameraStateMap[cam.id].groups = pairGroups[cam.id];
        }
      }
    },
    saveCurrentCameraState() {
      if (!this.currentCameraId) return;
      if (!this.cameraStateMap[this.currentCameraId]) {
        this.$set(this.cameraStateMap, this.currentCameraId, {
          groups: [],
          xraySelectedAnnoId: null,
          cameraSelectedAnnoId: null,
          editingGroup: null,
        });
      }
      const state = this.cameraStateMap[this.currentCameraId];
      state.xraySelectedAnnoId = this.xraySelectedAnnoId;
      state.cameraSelectedAnnoId = this.cameraSelectedAnnoId;
      state.editingGroup = this.editingGroup ? { ...this.editingGroup } : null;
    },
    getCameraState(cameraId) {
      if (!this.cameraStateMap[cameraId]) {
        this.$set(this.cameraStateMap, cameraId, {
          groups: [],
          xraySelectedAnnoId: null,
          cameraSelectedAnnoId: null,
          editingGroup: null,
        });
      }
      return this.cameraStateMap[cameraId];
    },
    restoreCameraState(cameraId) {
      const state = this.cameraStateMap[cameraId];
      if (state) {
        this.xraySelectedAnnoId = state.xraySelectedAnnoId;
        this.cameraSelectedAnnoId = state.cameraSelectedAnnoId;
        this.editingGroup = state.editingGroup ? { ...state.editingGroup } : null;
      } else {
        this.xraySelectedAnnoId = null;
        this.cameraSelectedAnnoId = null;
        this.editingGroup = null;
      }
    },
    onXrayAnnoClick(annoId) {
      this.xraySelectedAnnoId = this.xraySelectedAnnoId === annoId ? null : annoId;
    },
    onCameraAnnoClick(annoId) {
      this.cameraSelectedAnnoId = this.cameraSelectedAnnoId === annoId ? null : annoId;
    },
    async savePair() {
      if (!this.xraySelectedAnnoId || !this.cameraSelectedAnnoId) return;

      const matchTaskImageId = this.currentData.id;
      const state = this.getCameraState(this.currentCameraId);
      const groups = state.groups;

      if (this.editingGroup) {
        // Check that the new annotations are not used by other groups
        const otherGroups = groups.filter(g => g.pairId !== this.editingGroup.pairId);
        if (otherGroups.some(g => g.xrayAnnotationId === this.xraySelectedAnnoId)) {
          this.$message.warning('该X光框已被其他组使用');
          return;
        }
        if (otherGroups.some(g => g.cameraAnnotationId === this.cameraSelectedAnnoId)) {
          this.$message.warning('该视频框已被其他组使用');
          return;
        }

        // Update existing pair
        await api.updateMatchTaskPair(this.matchTaskId, matchTaskImageId, this.editingGroup.pairId, {
          xrayAnnotationId: this.xraySelectedAnnoId,
          cameraAnnotationId: this.cameraSelectedAnnoId,
        });

        // Update local state
        const grp = groups.find(g => g.pairId === this.editingGroup.pairId);
        if (grp) {
          grp.xrayAnnotationId = this.xraySelectedAnnoId;
          grp.cameraAnnotationId = this.cameraSelectedAnnoId;
        }
      } else {
        // Check for duplicates
        if (groups.some(g => g.xrayAnnotationId === this.xraySelectedAnnoId)) {
          this.$message.warning('该X光框已被关联，不能重复选择');
          return;
        }
        if (groups.some(g => g.cameraAnnotationId === this.cameraSelectedAnnoId)) {
          this.$message.warning('该视频框已被关联，不能重复选择');
          return;
        }

        // Create new pair
        const res = await api.addMatchTaskPair(this.matchTaskId, matchTaskImageId, {
          xrayAnnotationId: this.xraySelectedAnnoId,
          cameraAnnotationId: this.cameraSelectedAnnoId,
        });

        const nextNo = groups.length ? Math.max(...groups.map(g => g.groupNo)) + 1 : 1;
        groups.push({
          pairId: res.data.id,
          groupNo: nextNo,
          xrayAnnotationId: this.xraySelectedAnnoId,
          cameraAnnotationId: this.cameraSelectedAnnoId,
          cameraImageId: this.currentCameraId,
        });
      }

      this.xraySelectedAnnoId = null;
      this.cameraSelectedAnnoId = null;
      this.editingGroup = null;
    },
    editGroup(grp) {
      this.saveCurrentCameraState();
      this.editingGroup = grp;
      this.xraySelectedAnnoId = grp.xrayAnnotationId;
      this.cameraSelectedAnnoId = grp.cameraAnnotationId;
    },
    async deleteGroup(grp) {
      try {
        await this.$confirm(`确认删除第 ${grp.groupNo} 组关联吗?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        });
      } catch {
        return;
      }

      const matchTaskImageId = this.currentData.id;
      await api.deleteMatchTaskPair(this.matchTaskId, matchTaskImageId, grp.pairId);

      const state = this.getCameraState(this.currentCameraId);
      state.groups = state.groups.filter(g => g.pairId !== grp.pairId);

      if (this.editingGroup && this.editingGroup.pairId === grp.pairId) {
        this.editingGroup = null;
        this.xraySelectedAnnoId = null;
        this.cameraSelectedAnnoId = null;
      }
    },
    prevCamera() {
      if (this.currentCameraIndex <= 0) return;
      this.saveCurrentCameraState();
      this.currentCameraIndex--;
      this.restoreCameraState(this.currentCameraId);
    },
    nextCamera() {
      if (this.currentCameraIndex >= this.cameraCount - 1) return;
      this.saveCurrentCameraState();
      this.currentCameraIndex++;
      this.restoreCameraState(this.currentCameraId);
    },
    async finishImage() {
      this.loading = true;
      try {
        await api.finishMatchTaskImage(this.matchTaskId, this.currentData.id);
        this.cameraStateMap = {};
        await this.loadImage();
      } finally {
        this.loading = false;
      }
    },
    backToList() {
      this.$router.push('/match-task/list');
    },
  },
};
</script>

<style scoped>
.match-annotate-page {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 12px;
  gap: 12px;
  box-sizing: border-box;
}

/* Top section */
.top-section {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 12px;
}
.xray-card {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  overflow: hidden;
  position: relative;
}
.xray-card .card-body {
  flex: 1;
  position: relative;
  min-height: 0;
}

/* Sidebar */
.sidebar-card {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: hidden;
}
.sidebar-header {
  padding: 10px 12px 0;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}
.sidebar-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}
.group-item {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 2px;
  font-size: 13px;
  cursor: default;
}
.group-item:hover {
  background: #f0f2f5;
}
.group-item.editing {
  background: #e6f7ff;
  box-shadow: inset 0 0 0 1px #91d5ff;
}
.group-no {
  font-weight: 600;
  color: #5d89c0;
  margin-right: 6px;
  min-width: 20px;
  flex-shrink: 0;
}
.group-info {
  flex: 1;
  color: #666;
  font-size: 12px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 4px;
}
.group-actions {
  flex-shrink: 0;
  display: flex;
  white-space: nowrap;
}
.group-actions .el-button--text.el-button--mini {
  min-width: 40px;
  width: auto;
  padding: 4px 2px;
  margin-left: 0 !important;
}

/* Shared card style */
.xray-card,
.sidebar-card,
.camera-card {
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.xray-top-left {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  pointer-events: none;
}
.xray-header {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  background: rgba(255, 255, 255, 0.85);
  padding: 2px 8px;
  border-radius: 3px;
  line-height: 24px;
  pointer-events: none;
}
.xray-actions {
  background: rgba(255, 255, 255, 0.9);
  padding: 6px 8px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  pointer-events: auto;
}
.camera-top-left {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  pointer-events: none;
}
.camera-header {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  background: rgba(255, 255, 255, 0.85);
  padding: 2px 8px;
  border-radius: 3px;
  line-height: 24px;
  pointer-events: none;
}
.camera-actions {
  background: rgba(255, 255, 255, 0.9);
  padding: 6px 8px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  pointer-events: auto;
}
.camera-actions .el-button {
  margin: 0;
}
.empty-hint {
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 13px;
}

/* Camera card */
.camera-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.camera-card .card-body {
  flex: 1;
  position: relative;
  min-height: 0;
}
</style>
